import { memo, useState, useEffect } from 'react';

/**
 * AuthorAvatar component that displays either an avatar image or initials with colored background
 * @param {Object} props
 * @param {string} props.name - Author's full name
 * @param {string} props.avatar - Avatar image URL (optional)
 * @param {string} props.size - Size class (default: "w-10 h-10")
 * @param {string} props.textSize - Text size class (default: "text-sm")
 */
const AuthorAvatar = memo(({ name, avatar, size = "w-10 h-10", textSize = "text-sm" }) => {
  const [imageError, setImageError] = useState(false);

  // Reset error state when avatar URL changes
  useEffect(() => {
    setImageError(false);
  }, [avatar]);
  // Generate initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    // Take first and last name initials
    const first = parts[0].charAt(0).toUpperCase();
    const last = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${first}${last}`;
  };

  // Generate consistent color based on name
  const getColorFromName = (name) => {
    if (!name) return 'bg-gray-500';
    
    const colors = [
      'bg-red-500',
      'bg-blue-500', 
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500',
    ];
    
    // Simple hash function to get consistent color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const initials = getInitials(name);
  const bgColor = getColorFromName(name);
  const hasValidAvatar = avatar && avatar !== null && avatar !== '' && !imageError;

  const handleImageError = () => {
    setImageError(true);
  };

  if (hasValidAvatar) {
    return (
      <img
        src={avatar}
        alt={`${name || 'Author'} avatar`}
        className={`${size} rounded-full object-cover`}
        onError={handleImageError}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full ${bgColor} flex items-center justify-center text-white font-medium ${textSize}`}
      title={name || 'Author'}
    >
      {initials}
    </div>
  );
});

AuthorAvatar.displayName = 'AuthorAvatar';

export default AuthorAvatar;
